import { Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Circle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Card, CardContent, Field, Input, LoadingState, Select, Textarea, useToast } from '@salesbee/ui';
import { errorMessage, trpc } from '../../lib/trpc';
import { setWorkspaceSlug, useWorkspaceSlug } from '../../lib/workspace';
import { Wordmark } from '../../shell/Logo';
import { clearPending, readPending, REGION_OPTIONS, TEMPLATE_HINT, TEMPLATE_OPTIONS, templateFrom } from './pages';

function Step({ done, title, children }: { done: boolean; title: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-4 py-6">
        <div className="flex items-center gap-3">
          {done ? <CheckCircle2 className="size-5 text-success" aria-label="Done" /> : <Circle className="size-5 text-muted-foreground" aria-label="To do" />}
          <h2 className="text-[17px] font-semibold">{title}</h2>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

/** First workspace (from sign-up) and the setup checklist. */
export function OnboardingPage() {
  const me = trpc.workspace.me.get.useQuery();
  const slug = useWorkspaceSlug();
  const create = trpc.workspace.create.useMutation();
  const utils = trpc.useUtils();
  const pending = readPending();
  const [name, setName] = useState(pending?.workspaceName ?? '');
  const [region, setRegion] = useState(pending ? `${pending.country ?? 'CA'}:${pending.region ?? 'ON'}` : 'CA:ON');
  const [plan, setPlan] = useState<'sales_starter' | 'sales_growth'>(pending?.plan ?? 'sales_starter');
  const [template, setTemplate] = useState(templateFrom(pending?.template));

  const workspaces = me.data?.workspaces ?? [];
  useEffect(() => {
    if (workspaces.length && (!slug || !workspaces.some((w) => w.slug === slug))) setWorkspaceSlug(workspaces[0]!.slug);
  }, [workspaces, slug]);

  if (me.isLoading) return <LoadingState className="min-h-dvh" />;
  const createWorkspace = () => {
    const [country, r] = region.split(':') as ['CA' | 'US', string];
    create.mutate(
      { name, plan, country, region: r, template },
      {
        onSuccess: async (w) => {
          clearPending();
          setWorkspaceSlug(w.slug);
          await utils.workspace.me.get.invalidate();
        },
      },
    );
  };

  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4">
        <Wordmark />
        {workspaces.length ? (
          <Button asChild variant="ghost">
            <Link to="/dashboard">Go to dashboard</Link>
          </Button>
        ) : null}
      </header>
      <main className="mx-auto max-w-3xl space-y-5 px-4 pb-20 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.03em]">Set up SalesBee</h1>
        {workspaces.length === 0 || !slug ? (
          <Card>
            <CardContent className="space-y-4 py-6">
              <p className="text-muted-foreground">Create your workspace to start the 7-day trial.</p>
              {create.error ? <Alert variant="destructive">{errorMessage(create.error)}</Alert> : null}
              <Field label="Company name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Province or state">
                <Select value={region} onChange={(e) => setRegion(e.target.value)} options={REGION_OPTIONS} />
              </Field>
              <Field label="Plan">
                <Select value={plan} onChange={(e) => setPlan(e.target.value as typeof plan)} options={[{ value: 'sales_starter', label: 'SalesBee Starter' }, { value: 'sales_growth', label: 'SalesBee Growth' }]} />
              </Field>
              <Field label="Industry template" description={TEMPLATE_HINT}>
                <Select value={template} onChange={(e) => setTemplate(templateFrom(e.target.value))} options={TEMPLATE_OPTIONS} />
              </Field>
              <Button onClick={createWorkspace} loading={create.isPending} disabled={!name.trim()}>
                Create workspace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Checklist key={slug} />
        )}
      </main>
    </div>
  );
}

function Checklist() {
  const current = trpc.workspace.current.useQuery();
  const settings = trpc.workspace.settings.get.useQuery();
  const update = trpc.workspace.settings.update.useMutation();
  const search = trpc.prospecting.search.useMutation();
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [callerId, setCallerId] = useState('');
  const [callerName, setCallerName] = useState('');
  const [disclosure, setDisclosure] = useState('');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('20:00');
  const [prospectIndustry, setProspectIndustry] = useState('');
  useEffect(() => {
    if (!settings.data) return;
    setIndustry(settings.data.industry ?? '');
    setWebsite(settings.data.website ?? '');
    setCallerId(settings.data.outbound.callerIdNumber ?? '');
    setCallerName(settings.data.outbound.callerIdName ?? '');
    setDisclosure(settings.data.outbound.disclosureScript);
  }, [settings.data]);
  const preview = trpc.workspace.settings.previewDisclosure.useQuery({ script: disclosure }, { enabled: disclosure.length > 0 });

  if (!current.data || !settings.data) return <LoadingState />;
  const steps = current.data.onboarding.steps;
  const save = (patch: Parameters<typeof update.mutate>[0], message: string) =>
    update.mutate(patch, {
      onSuccess: async () => {
        success(message);
        await Promise.all([utils.workspace.current.invalidate(), utils.workspace.settings.get.invalidate()]);
      },
      onError: (e) => error('Could not save', errorMessage(e)),
    });

  return (
    <div className="space-y-5">
      <p className="text-muted-foreground">
        {current.data.name} · {current.data.entitlements.planName}
        {current.data.entitlements.trialDaysLeft !== null ? ` · ${current.data.entitlements.trialDaysLeft} trial days left` : ''}
      </p>
      <Step done={steps.company} title="1. Your company">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Industry you sell into">
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. Property management" />
          </Field>
          <Field label="Website">
            <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />
          </Field>
        </div>
        <Button size="sm" onClick={() => save({ industry, website, onboarding: { company: true }, icp: industry ? { industries: [industry] } : undefined }, 'Company saved')}>
          Save
        </Button>
      </Step>
      <Step done={steps.callerId} title="2. Caller ID number">
        <p className="text-sm text-muted-foreground">Every call shows a real, reachable number. Calls are blocked until it is set.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Caller ID (Canada/US)">
            <Input value={callerId} onChange={(e) => setCallerId(e.target.value)} placeholder="+1 416 555 0100" />
          </Field>
          <Field label="Caller name">
            <Input value={callerName} onChange={(e) => setCallerName(e.target.value)} />
          </Field>
        </div>
        <Button size="sm" onClick={() => save({ outbound: { callerIdNumber: callerId, callerIdName: callerName || null } }, 'Caller ID saved')}>
          Save
        </Button>
      </Step>
      <Step done={steps.disclosure} title="3. Call disclosure script">
        <p className="text-sm text-muted-foreground">Spoken first on every AI call: it must say it is an AI, name your business and say the call may be recorded. Have counsel review it before live calling.</p>
        <Textarea rows={4} value={disclosure} onChange={(e) => setDisclosure(e.target.value)} />
        {preview.data && !preview.data.ok ? <Alert variant="warning">{preview.data.problems.join(' ')}</Alert> : null}
        {preview.data?.ok ? <Alert variant="success">Spoken as: “{preview.data.rendered}”</Alert> : null}
        <Button size="sm" onClick={() => save({ outbound: { disclosureScript: disclosure }, onboarding: { disclosure: true } }, 'Disclosure saved')}>
          Save
        </Button>
      </Step>
      <Step done={steps.callingHours} title="4. Calling hours">
        <p className="text-sm text-muted-foreground">
          The legal window in the callee's local time always applies (Canada: weekdays 9:00-21:30, weekends 10:00-18:00; US: 8:00-21:00). You can narrow it for weekdays.
        </p>
        <div className="grid max-w-sm grid-cols-2 gap-4">
          <Field label="Weekdays from">
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="until">
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <Button size="sm" onClick={() => save({ outbound: { weekdayWindow: { start, end } }, onboarding: { callingHours: true } }, 'Calling hours saved')}>
          Save
        </Button>
      </Step>
      <Step done={steps.firstSearch} title="5. First prospect search">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Industry" className="min-w-64 flex-1">
            <Input value={prospectIndustry} onChange={(e) => setProspectIndustry(e.target.value)} placeholder="e.g. Roofing contractors" />
          </Field>
          <Button
            loading={search.isPending}
            disabled={!prospectIndustry.trim()}
            onClick={() =>
              search.mutate(
                { query: { kind: 'business', industry: prospectIndustry, keywords: [], titles: [], country: (settings.data.country === 'US' ? 'US' : 'CA') as 'CA' | 'US', region: settings.data.region ?? undefined, limit: 25 }, newListName: `${prospectIndustry} prospects` },
                {
                  onSuccess: async (r) => {
                    success(`Saved ${r.saved} prospects`);
                    await utils.workspace.current.invalidate();
                  },
                  onError: (e) => error('Search failed', errorMessage(e)),
                },
              )
            }
          >
            Search
          </Button>
        </div>
      </Step>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => save({ onboarding: { dismissed: true } }, 'Checklist hidden')}>
          Skip for now
        </Button>
        <Button onClick={() => void navigate({ to: '/dashboard' })}>Go to dashboard</Button>
      </div>
    </div>
  );
}
